import { useMemo } from "react"
import { useIsAiTermsAccepted, useIsAiChatFeatureEnabled, useIsAiEverywhereEnabled } from "resources/account"
import { AiContext } from "../context/ai-context"
import AiTeammateWithButtonToggle from "../components/ai-toggle-button"

import "./with-ai-context.scss"

function useStableTypeId(type, id) {
  return useMemo(
    () => {
      if (!id) return undefined
      const normalizedType = type === "SWAP" ? "BASKET" : type
      return { type: normalizedType, id }
    },
    [type, id],
  )
}

export function withAiContext(useAdaptedData) {
  return function wrapWithAiContext(WrappedComponent) {
    function ComponentWithAiContext(props) {
      const rawData = useAdaptedData(props)
      const isAiTermsAccepted = useIsAiTermsAccepted()
      const isAiEnabled = useIsAiChatFeatureEnabled()
      const isAiEverywhereEnabled = useIsAiEverywhereEnabled()

      const instrument = useStableTypeId(rawData.instrument?.type, rawData.instrument?.id)

      const base = useStableTypeId(rawData.base?.type, rawData.base?.id)

      const adaptedData = useMemo(() => ({
        modelId: rawData.modelId,
        instrument,
        base,
        startDate: rawData.startDate,
        endDate: rawData.endDate,
        isPreferencesLoaded: rawData.isPreferencesLoaded,
      }), [
        rawData.modelId,
        instrument,
        base,
        rawData.startDate,
        rawData.endDate,
        rawData.isPreferencesLoaded,
      ])

      const canShowAiButton = isAiEnabled && isAiTermsAccepted && isAiEverywhereEnabled && adaptedData?.isPreferencesLoaded

      return (
        <AiContext.Provider value={adaptedData}>
          {canShowAiButton && (
            <div className="with-ai-context-button">
              <AiTeammateWithButtonToggle size="small" />
            </div>
          )}
          <WrappedComponent {...props} />
        </AiContext.Provider>
      )
    }

    ComponentWithAiContext.displayName = `withAiContext(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`

    return ComponentWithAiContext
  }
}
